import { ArrowLeft } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { ErroApi } from '@/nucleo/auth/cliente-api';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';
import { ErrorState } from '@/ui/ErrorState';
import { Spinner } from '@/ui/Spinner';

import { CamposDoCadastro } from './CamposDoCadastro';
import type { LinhaDoCadastro } from './cadastros.contrato';
import {
  mensagemDoErro,
  useAlterarCadastro,
  useCriarCadastro,
  useDetalhePorSlug,
} from './cadastros.dados';
import type { DefinicaoDoCadastro } from './cadastros.definicoes';
import { useFormularioDoCadastro } from './useFormularioDoCadastro';

export interface FormularioDeCadastroProps {
  modo: 'criar' | 'editar';
  definicao: DefinicaoDoCadastro;
}

const PREFIXO = 'cadastro';

/**
 * Formulário de cadastro de referência em página inteira (seguradoras), nos dois
 * modos: criar e editar. Grava de verdade pela API.
 *
 * O que a tela faz antes de enviar: confere obrigatório, formato de CNPJ, de
 * e-mail e tamanho, aponta o campo errado com foco e mensagem ligados a ele. O
 * que a API recusa depois (CNPJ já cadastrado, dígito verificador errado,
 * registro alterado por outra pessoa) aparece no alto do formulário, com a frase
 * que diz o que fazer. Só sai da tela quando o registro foi salvo.
 */
export function FormularioDeCadastro({ modo, definicao }: FormularioDeCadastroProps) {
  const { slug = '' } = useParams<{ slug: string }>();
  const editando = modo === 'editar';
  const detalhe = useDetalhePorSlug(definicao.grupo, definicao.chave, slug, editando);

  if (editando && detalhe.isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center gap-3 py-16">
          <Spinner />
          <p className="text-sm text-muted-foreground">Carregando o registro…</p>
        </CardContent>
      </Card>
    );
  }

  if (editando && detalhe.isError) {
    const naoExiste = detalhe.error instanceof ErroApi && detalhe.error.situacao === 404;
    return naoExiste ? (
      <ErrorState
        variant="resource-not-found"
        title={`${definicao.nomeDaEntidade} não encontrada`}
        description="Não é possível editar um registro que não existe."
        primaryAction={{ label: definicao.titulo, to: definicao.caminho }}
      />
    ) : (
      <ErrorState
        variant="load-failed"
        title="Não foi possível carregar"
        description={mensagemDoErro(detalhe.error, 'Tente de novo em instantes.')}
        primaryAction={{ label: 'Tentar de novo', onClick: () => void detalhe.refetch() }}
        secondaryAction={{ label: definicao.titulo, to: definicao.caminho, variant: 'outline' }}
      />
    );
  }

  return <Corpo modo={modo} definicao={definicao} registro={detalhe.data} />;
}

function Corpo({
  modo,
  definicao,
  registro,
}: FormularioDeCadastroProps & { registro?: LinhaDoCadastro }) {
  const navegar = useNavigate();
  const criar = useCriarCadastro(definicao.grupo, definicao.chave);
  const alterar = useAlterarCadastro(definicao.grupo, definicao.chave);
  const formulario = useFormularioDoCadastro(definicao, registro);
  const [recusa, setRecusa] = useState<string | null>(null);
  const enviando = criar.isPending || alterar.isPending;
  const entidade = definicao.nomeDaEntidade.toLowerCase();
  const nova = definicao.genero === 'a';

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    setRecusa(null);
    const entrada = formulario.conferir(PREFIXO);
    if (!entrada) return;

    const aoSalvar = {
      onSuccess: (salvo: LinhaDoCadastro) => {
        toast.success(`${definicao.nomeDaEntidade} ${nova ? 'salva' : 'salvo'}.`);
        navegar(`${definicao.caminho}/${salvo.slug ?? ''}`);
      },
      onError: (erro: unknown) =>
        setRecusa(mensagemDoErro(erro, 'Não foi possível salvar. Tente de novo em instantes.')),
    };
    if (registro) alterar.mutate({ id: registro.id, entrada }, aoSalvar);
    else criar.mutate(entrada, aoSalvar);
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={definicao.caminho}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {definicao.titulo}
        </Link>
      </div>

      <header className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">
          {modo === 'criar' ? `${nova ? 'Nova' : 'Novo'} ${entidade}` : `Editar ${entidade}`}
        </h1>
        <p className="text-sm text-muted-foreground">
          {modo === 'criar'
            ? `Cadastro ${nova ? 'de uma nova' : 'de um novo'} ${entidade} do TEG.`
            : 'Alteração dos dados do cadastro.'}
        </p>
      </header>

      <Card>
        <CardContent className="pt-6">
          <form noValidate onSubmit={enviar} className="space-y-8">
            {recusa && (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{recusa}</AlertDescription>
              </Alert>
            )}

            <CamposDoCadastro
              campos={definicao.campos}
              valores={formulario.valores}
              problemas={formulario.problemas}
              aoAlterar={formulario.alterar}
              prefixo={PREFIXO}
            />

            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" disabled={enviando} aria-busy={enviando}>
                {enviando ? 'Salvando…' : 'Salvar'}
              </Button>
              <Button asChild variant="outline">
                <Link to={definicao.caminho}>Cancelar</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default FormularioDeCadastro;
